import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import type { DepartmentsListRegionFilterData } from './types';

const NORTH_AMERICA_OFFICE_ID = 4012115009; // North America
const EUROPE_OFFICE_ID = 4006486009; // Europe

export interface DepartmentsListRegionFiltersProps {
  readonly onFilterChange: (value: number | null) => void;
  readonly selectedFilter: number | null;
}

const filters: DepartmentsListRegionFilterData[] = [
  {
    id: 0,
    text: 'careers_filter_view_all',
    value: -1,
  },
  {
    id: 1,
    text: 'careers_filter_north_america',
    value: NORTH_AMERICA_OFFICE_ID,
  },
  {
    id: 2,
    text: 'careers_filter_europe',
    value: EUROPE_OFFICE_ID,
  },
];

interface DepartmentsListRegionFilterProps
  extends DepartmentsListRegionFilterData {
  readonly onChange: (officeId: number | null) => void;
  readonly selectedFilter: number | null;
}

const DepartmentsListRegionFilter = ({
  id,
  onChange,
  selectedFilter,
  text,
  value,
}: DepartmentsListRegionFilterProps) => {
  const t = useTranslations();
  const isSelected = selectedFilter === value;

  const handleChange = () => {
    if (value === -1) {
      onChange(null);
    } else {
      onChange(value);
    }
  };

  return (
    <div
      className="flex flex-row-reverse items-center gap-x-4"
      key={`region-${id}`}
    >
      <label
        className={cx('max-md:py-4', {
          'text-blue-200': isSelected,
          'text-sandstone-500': !isSelected,
        })}
        data-cy={`filter-label-${id}`}
        htmlFor={`filter-${id}`}
      >
        {t(text)}
      </label>
      <input
        checked={isSelected}
        className={cx(
          'max-md:sr-only md:block',
          'size-5 cursor-pointer appearance-none rounded-full transition-all',
          'border-2 border-solid border-sandstone-500',
          'checked:bg-blue-200',
        )}
        data-cy={`filter-radio-${id}`}
        id={`filter-${id}`}
        name="radio"
        onChange={handleChange}
        type="radio"
        value={value}
      />
    </div>
  );
};

const DepartmentsListRegionFilters = ({
  onFilterChange,
  selectedFilter,
}: DepartmentsListRegionFiltersProps) => {
  return (
    <div className="flex gap-x-4 md:mb-4 md:justify-end md:gap-x-12">
      {filters.map((filter) => (
        <DepartmentsListRegionFilter
          {...filter}
          key={filter.id}
          onChange={onFilterChange}
          selectedFilter={selectedFilter ?? -1}
        />
      ))}
    </div>
  );
};

export default DepartmentsListRegionFilters;
